import { Component, DOCUMENT, inject, input } from '@angular/core';

import { IRowNode } from 'ag-grid-enterprise';
import { fromEvent } from 'rxjs';

import { ENotificationCode, LAZY_SERVICE_TOKEN } from '@camelot/notification';
import { TranslatePipe } from '@camelot/translation';
import {
  BadgeComponent,
  ButtonComponent,
  ButtonSize,
  CamOverlayPanelComponent,
  OverlayMenuConfig,
  TextComponent,
} from '@camelot/ui';

import { EditabledShortcut } from '../../../../models/types';
import { GridRow } from '../../../../services/dto/grid-row';
import { InsertPosition } from '../../../../services/dto/insert-position';
import { CamTranslationGrid } from '../../../../translation.service';
import { BaseGridComponent } from '../../../base-grid.component';
import { ARROW_POSITION, EDITABLED_SHORTCUTS, EDITABLED_SHORTCUT_HINTS } from '../editabled-shortcuts';

@Component({
  imports: [BadgeComponent, ButtonComponent, CamOverlayPanelComponent, TextComponent, TranslatePipe],
  selector: 'cam-editabled-shortcuts-info',
  styleUrl: './editabled-shortcuts-info.component.scss',
  templateUrl: './editabled-shortcuts-info.component.html',
})
export class EditabledShortcutsInfoComponent extends BaseGridComponent<GridRow> {
  label = input<string>();

  size = input<ButtonSize>('md');

  readonly shortcutHints = EDITABLED_SHORTCUT_HINTS;

  public readonly panelConfig: OverlayMenuConfig = { matchTriggerWidth: false, maxHeight: 600, width: 320 };

  // Retrieve the shared grid instance, never create it (the grid owns creation).
  protected override _createGrid = false;

  private _document = inject(DOCUMENT);
  private _notificationService = inject(LAZY_SERVICE_TOKEN);
  private _pending: { node: IRowNode<GridRow>; shortcut: EditabledShortcut } | null = null;

  constructor() {
    super();
    CamTranslationGrid.getInstance();
  }

  override ngOnInit() {
    super.ngOnInit();
    this._registerSubscription(
      fromEvent<KeyboardEvent>(this._document, 'keydown').subscribe(event => this._onKeyDown(event))
    );
    this._registerSubscription(
      fromEvent<KeyboardEvent>(this._document, 'keyup').subscribe(event => this._onKeyUp(event))
    );
  }

  override ngOnDestroy() {
    super.ngOnDestroy();
    this._pending = null;
  }

  private _onKeyDown(event: KeyboardEvent) {
    // Ignore OS auto-repeat (holding the key down) — a shortcut must fire once per physical press.
    if (event.repeat) {
      return;
    }

    if (this._isEditing() || this._isEditableTarget(event.target) || this._isOtherGridFocused()) {
      return;
    }

    // Third key of a position combo: all three keys must be down when the arrow is pressed.
    if (this._pending && event.altKey && event.code in ARROW_POSITION) {
      event.preventDefault();
      const { node, shortcut } = this._pending;
      this._pending = null;
      this._run(shortcut, node, ARROW_POSITION[event.code]);
      return;
    }

    if (!event.altKey) {
      return;
    }

    const shortcut = EDITABLED_SHORTCUTS.find(item => item.code === event.code);
    if (!shortcut) {
      return;
    }
    event.preventDefault();

    // A new combo replaces any pending one — a position combo never fires without its arrow.
    this._pending = null;

    const node = this._getActiveNode();
    if (!node?.data) {
      this._notify('grid.editabled.shortcut.no-selection');
      return;
    }

    if (shortcut.hasPosition) {
      this._pending = { node, shortcut };
    } else {
      this._run(shortcut, node);
    }
  }

  private _onKeyUp(event: KeyboardEvent) {
    // Releasing `Alt` or the combo key disarms the pending combo without firing.
    if (this._pending && (event.key === 'Alt' || event.code === this._pending.shortcut.code)) {
      this._pending = null;
    }
  }

  private _run(shortcut: EditabledShortcut, node: IRowNode<GridRow>, position = InsertPosition.Below) {
    const data = node.data;
    if (!data) {
      this._notify('grid.editabled.shortcut.no-selection');
      return;
    }

    // une action absente du menu (non fournie ou non autorisee) est refusee, comme dans le menu contextuel
    const config = this._grid.shortcutMenu.find(item => item.action === shortcut.action);
    const sub = config?.subMenu?.find(item =>
      shortcut.targetType !== undefined ? item.targetType === shortcut.targetType : item.position === position
    );
    if (!config || config.disabled?.(data, node) || (config.subMenu && (!sub || sub.disabled?.(data, node)))) {
      this._notify('grid.editabled.shortcut.not-allowed');
      return;
    }

    this._grid.shortcutAction$.next({ id: data.id, position, shortcut });
  }

  private _getActiveNode(): IRowNode<GridRow> | null {
    const api = this._grid?.api;
    if (!api) {
      return null;
    }
    const selected = api.getSelectedNodes();
    if (selected.length > 0) {
      return selected[0];
    }
    const focused = api.getFocusedCell();
    if (focused) {
      return api.getDisplayedRowAtIndex(focused.rowIndex) ?? null;
    }
    return null;
  }

  private _isEditing(): boolean {
    return (this._grid?.api?.getEditingCells()?.length ?? 0) > 0;
  }

  // avec deux grilles sur la page, seule celle qui a le focus reagit
  private _isOtherGridFocused(): boolean {
    const api = this._grid?.api;
    const focusedGrid = (this._document.activeElement as HTMLElement | null)?.closest('[grid-id]');
    return !!api && !!focusedGrid && focusedGrid.getAttribute('grid-id') !== api.getGridId();
  }

  private _isEditableTarget(target: EventTarget | null): boolean {
    const element = target as HTMLElement | null;
    if (!element) {
      return false;
    }
    const tag = element.tagName;
    return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || element.isContentEditable;
  }

  private _notify(message: string) {
    this._notificationService.addNotification(message, ENotificationCode.warning);
  }
}
